// Student Dashboard API Service
import axios from 'axios';
import { API_CONFIG } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

// Create axios instance with auth token
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Dashboard API endpoints
export const DashboardAPI = {
  // Get comprehensive dashboard stats
  getComprehensiveStats: async () => {
    try {
      const response = await api.get('/student-dashboard/comprehensive-stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching comprehensive stats:', error);
      throw error;
    }
  },

  // Get student's classes
  getMyClasses: async () => {
    try {
      const response = await api.get('/student-dashboard/my-classes');
      return response.data;
    } catch (error) {
      console.error('Error fetching classes:', error);
      throw error;
    }
  },

  // Get student's assignments
  getMyAssignments: async (statusFilter = null) => {
    try {
      const params = statusFilter ? { status_filter: statusFilter } : {};
      const response = await api.get('/student-dashboard/my-assignments', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching assignments:', error);
      throw error;
    }
  },

  // Get today's lessons
  getTodaysLessons: async () => {
    try {
      const response = await api.get('/student-dashboard/today-lessons');
      return response.data;
    } catch (error) {
      console.error('Error fetching today\'s lessons:', error);
      throw error;
    }
  },

  // Get published lessons
  getPublishedLessons: async (page = 1, limit = 12) => {
    try {
      const response = await api.get('/lesson-plans/student/lessons', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching published lessons:', error);
      throw error;
    }
  },

  // Get assigned assessments
  getAssignedAssessments: async () => {
    try {
      console.log('[API] Fetching assessments from /student-assessments/student/assessments');
      const response = await api.get('/student-assessments/student/assessments');
      console.log('[API] Assessments response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching assessments:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  // Get performance analytics
  getPerformanceAnalytics: async () => {
    try {
      const response = await api.get('/student-dashboard/performance-analytics');
      return response.data;
    } catch (error) {
      console.error('Error fetching performance analytics:', error);
      throw error;
    }
  },

  // Get upcoming events
  getUpcomingEvents: async () => {
    try {
      const response = await api.get('/student-dashboard/upcoming-events');
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      throw error;
    }
  },

  // Get study progress
  getStudyProgress: async () => {
    try {
      const response = await api.get('/student-dashboard/study-progress');
      return response.data;
    } catch (error) {
      console.error('Error fetching study progress:', error);
      throw error;
    }
  },

  // Get quick stats
  getQuickStats: async () => {
    try {
      const response = await api.get('/student-dashboard/quick-stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching quick stats:', error);
      throw error;
    }
  },

  // Call working attendance endpoint directly
  getMyAttendance: async () => {
    try {
      const response = await api.get('/attendance/student/my-attendance');
      return response.data;
    } catch (error) {
      console.error('Error fetching my attendance:', error);
      throw error;
    }
  },

  // Call working calendar endpoint directly
  getCalendarEvents: async () => {
    try {
      console.log('[API] Fetching events from /calendar/student-events');
      const response = await api.get('/calendar/student-events');
      console.log('[API] Events response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  // Call working lessons endpoint directly
  getAllLessons: async (page = 1, limit = 50) => {
    try {
      console.log('[API] Fetching lessons from /lesson-plans/student/lessons');
      const response = await api.get('/lesson-plans/student/lessons', {
        params: { page, limit }
      });
      console.log('[API] Lessons response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching all lessons:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  // Get student profile
  getStudentProfile: async () => {
    try {
      const response = await api.get('/student/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching student profile:', error);
      throw error;
    }
  },

  // Get comprehensive dashboard data in ONE call
  getDashboardOverview: async () => {
    try {
      console.log('[API] Fetching dashboard overview from /student-dashboard/dashboard-overview');
      const response = await api.get('/student-dashboard/dashboard-overview');
      console.log('[API] Dashboard overview response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard overview:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },
};

export default DashboardAPI;
